/**
 * kbFormats — owner-agnostic KB file-format rules (allowed types, size cap, editability).
 * Extracted from the old useAssetKbFiles so useKb + the panel share one source; these
 * rules are identical for every owner (asset / uc / storyline).
 */
import { type KbContentType } from '../../../client/IvtHubClient';

/** filename extension → the exact MIME the backend allows (design §4.6). All are
 *  natively ingested by Managed KB. Only txt/md are editable in place. */
const EXT_TO_CONTENT_TYPE: Record<string, KbContentType> = {
  pdf: 'application/pdf' as KbContentType,
  doc: 'application/msword' as KbContentType,
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' as KbContentType,
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' as KbContentType,
  xls: 'application/vnd.ms-excel' as KbContentType,
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' as KbContentType,
  csv: 'text/csv' as KbContentType,
  html: 'text/html' as KbContentType,
  txt: 'text/plain' as KbContentType,
  md: 'text/markdown' as KbContentType,
};

/** The only text formats whose content can be edited in-place (txt/md). */
const EDITABLE_EXTS = ['txt', 'md'];

/** Accept attribute for the file picker (the allowed upload formats). */
export const KB_UPLOAD_ACCEPT = '.pdf,.doc,.docx,.pptx,.xls,.xlsx,.csv,.html,.txt,.md';

/** Bedrock hard limit — a bigger file's confirm is rejected server-side (§4). */
export const KB_MAX_FILE_BYTES = 500 * 1024 * 1024;

/** Human-readable size cap, kept in one place so the guard + all copy stay in sync. */
export const KB_MAX_FILE_LABEL = '500MB';

/** Resolve a file's content type from its extension; null = unsupported format. */
export function contentTypeForFile(filename: string): KbContentType | null {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  return EXT_TO_CONTENT_TYPE[ext] ?? null;
}

/** True if the file's content can be edited in place (txt/md only). */
export function isEditableFile(filename?: string): boolean {
  const ext = (filename ?? '').toLowerCase().split('.').pop() ?? '';
  return EDITABLE_EXTS.includes(ext);
}
